// ============================================
// Background Rendering Module
// Background drawing and stage management functions
// ============================================

import type p5 from 'p5';
import { getP5 } from '../../core/p5-instance';
import {CONFIG} from '../../config/game-config';
import {
    getCurrentStageIndex,
    getDt,
    getGameState,
    getIsStageTransitioning,
    getPrevStageIndex,
    getStageNameDisplayTimer,
    getStageParticleAlpha,
    getStageTransitionTimer,
    setCurrentStageIndex,
    setDistantGiantY,
    setIsStageTransitioning,
    setPrevStageIndex,
    setStageNameDisplayTimer,
    setStageParticleAlpha,
    setStageTransitionTimer
} from '../../store/index';
import {drawStageEffects} from './stage-effects';
import type { StageConfig } from '../../types/game-types';

// Local type definitions (extended from game-types)
type BgStar = {
    x: number;
    y: number;
    size: number;
    speed: number;
    alpha: number;
    twinkleSpeed: number;
    twinkleOffset: number;
    brightness: number;
    layer: number;
    baseAlpha: number;
};

type BgBrightStar = BgStar & {
    glowSize: number;
    color: number[];
    flare: boolean;
};

type BgAsteroid = {
    x: number;
    y: number;
    size: number;
    speed: number;
    rot: number;
    rotSpeed: number;
    type: number;
    driftX: number;
    rotation: number;
    vertices: number;
    roughness: number;
    shapeOffset: number;
    layer: number;
    colorVar: number;
    craterCount: number;
    craterSeeds: number[];
};

type BgNebulaLayer = {
    x: number;
    y: number;
    baseY: number;
    size: number;
    speed: number;
    offset: number;
    alpha: number;
    color: p5.Color;
    rot: number;
    rotSpeed: number;
    type: number;
    mainColor: number[];
    secondaryColor: number[];
    turbulence: number;
    spiralCount: number;
    dustDensity: number;
};

type Star = {
    x: number;
    y: number;
    size: number;
    speed: number;
    brightness: number;
};

type LowOrbitPlanetMask = {
    x: number;
    y: number;
    r: number;
    r2: number;
};

// Background stars and elements
let bgStars: BgStar[] = [];
let bgBrightStars: BgBrightStar[] = [];
let bgAsteroids: BgAsteroid[] = [];
let bgNebulaLayers: BgNebulaLayer[] = [];
let stars: Star[] = [];

// Low orbit planet mask for occlusion (local to background.ts only)
let lowOrbitPlanetMask: LowOrbitPlanetMask | null = null;

// Exports for getters/setters
export function getBgStars() {
    return bgStars;
}
export function getBgBrightStars() {
    return bgBrightStars;
}
export function getBgAsteroids() {
    return bgAsteroids;
}
export function getBgNebulaLayers() {
    return bgNebulaLayers;
}
export function getLowOrbitPlanetMask() {
    return lowOrbitPlanetMask;
}
export function setLowOrbitPlanetMask(val: LowOrbitPlanetMask | null): void {
    lowOrbitPlanetMask = val;
}

// ============================================
// initStageBackground() - ステージ背景の初期化
// ============================================
export function initStageBackground() {
    const p = getP5();
    const bgCfg = CONFIG.stages.common;
    const gameAreaX = CONFIG.layout.gameAreaX;
    const gameAreaW = CONFIG.width;

    // 背景の星を生成（通常の星）- ゲームエリア内
    bgStars = [];
    for (let i = 0; i < bgCfg.bgStarsCount; i++) {
        bgStars.push({
            x: gameAreaX + p.random(gameAreaW),
            y: p.random(p.height),
            size: p.random(0.5, 2.0),
            speed: p.random(0.5, 1.5),
            alpha: p.random(0.5, 1.0),
            twinkleSpeed: p.random(0.03, 0.1),
            twinkleOffset: p.random(p.TWO_PI),
            brightness: p.random(60, 180),
            layer: p.floor(p.random(3)),
            baseAlpha: p.random(0.5, 1.0)
        });
    }

    // 明るい星を生成（Starfield用）- 少数で瞬き控えめ、手前に流れる
    bgBrightStars = [];
    for (let i = 0; i < bgCfg.bgBrightStarsCount; i++) {
        bgBrightStars.push({
            x: gameAreaX + p.random(gameAreaW),
            y: p.random(p.height),
            size: p.random(2.5, 4),
            speed: p.random(1.5, 3.5),  // 流れる速度
            alpha: p.random(0.8, 1.0),
            twinkleSpeed: p.random(0.015, 0.03),
            twinkleOffset: p.random(p.TWO_PI),
            brightness: p.random(200, 255),
            layer: 0,
            baseAlpha: p.random(0.8, 1.0),
            glowSize: p.random(10, 16),
            color: p.random() > 0.5 ? [255, 250, 240] : [240, 245, 255],
            flare: false
        });
    }

    // 小惑星を生成 - 動きの方向性をさらに強化
    bgAsteroids = [];
    for (let i = 0; i < bgCfg.bgAsteroidsCount; i++) {
        // 速度と方向をより多様に（さらに強化）
        const baseSpeed = p.random(0.3, 1.8);  // 速度のバリエーションをさらに広げる
        const driftAngle = p.random(-1.2, 1.2);  // 左右のドリフトをさらに強化
        bgAsteroids.push({
            x: gameAreaX + p.random(gameAreaW),
            y: p.random(p.height),
            size: p.random(12, 45),
            speed: baseSpeed,
            rot: p.random(p.TWO_PI),
            rotSpeed: p.random(-0.008, 0.008),  // 回転をさらに速く
            type: p.floor(p.random(3)),
            driftX: driftAngle,
            rotation: p.random(p.TWO_PI),
            vertices: p.floor(p.random(7, 12)),
            roughness: p.random(0.12, 0.28),
            shapeOffset: p.random(p.TWO_PI),
            layer: i < 5 ? 0 : (i < 10 ? 1 : 2),
            colorVar: p.random(-20, 20),
            craterCount: p.floor(p.random(2, 5)),
            craterSeeds: [p.random(100), p.random(100), p.random(100), p.random(100)]
        });
    }

    // 星雲レイヤーを生成（Deep Space用）- 本物の星雲をイメージ
    bgNebulaLayers = [];
    // 本物の星雲の色（赤/ピンク=水素、青/緑=酸素、茶=塵）
    const nebulaColors = [
        { main: [180, 60, 80], secondary: [220, 100, 120], name: 'emission' },   // 赤い発光星雲
        { main: [60, 80, 140], secondary: [100, 120, 180], name: 'reflection' }, // 青い反射星雲
        { main: [100, 50, 70], secondary: [140, 80, 100], name: 'dark' },        // 暗黒星雲
        { main: [80, 100, 130], secondary: [120, 140, 170], name: 'planetary' }, // 惑星状星雲
        { main: [150, 70, 90], secondary: [190, 110, 130], name: 'emission2' },  // 別の発光星雲
        { main: [70, 60, 100], secondary: [110, 100, 150], name: 'dark2' }       // 別の暗黒星雲
    ];
    for (let i = 0; i < bgCfg.bgNebulaCount; i++) {
        const nc = nebulaColors[i % nebulaColors.length];
        bgNebulaLayers.push({
            x: gameAreaX + p.random(gameAreaW * 0.1, gameAreaW * 0.9),
            y: p.random(-200, p.height * 0.4),
            baseY: p.random(-200, p.height * 0.4),
            size: p.random(250, 420),
            speed: p.random(0.04, 0.12),  // よりゆっくり漂う
            offset: p.random(p.TWO_PI),
            alpha: p.random(0.4, 0.8),
            color: p.color(nc.main[0], nc.main[1], nc.main[2]),
            rot: 0,
            rotSpeed: p.random(-0.001, 0.001),
            type: i % 3,
            mainColor: nc.main,
            secondaryColor: nc.secondary,
            turbulence: p.random(0.4, 0.8),
            // ガスの渦の数
            spiralCount: p.floor(p.random(2, 5)),
            dustDensity: p.random(0.3, 0.7)
        });
    }

    // 流れる星を生成 - ゲームエリア内
    stars = [];
    for (let i = 0; i < bgCfg.flowingStarsCount; i++) {
        stars.push({
            x: gameAreaX + p.random(gameAreaW),
            y: p.random(p.height),
            size: p.random(1, 3),
            speed: p.random(2, 6),
            brightness: p.random(180, 255)
        });
    }
}

// ============================================
// drawBackground() - 背景の描画
// ステージに応じた背景を描画
// ============================================
export function drawBackground() {
    const p = getP5();
    const gameState = getGameState();
    const dt = getDt();
    const stageCfg = getCurrentStageCfg();
    const isStageTransitioning = getIsStageTransitioning();
    const stageTransitionTimer = getStageTransitionTimer();
    const stageNameDisplayTimer = getStageNameDisplayTimer();

    // 遷移処理
    let transitionProgress = 1;
    if (isStageTransitioning) {
        transitionProgress = 1 - (stageTransitionTimer / CONFIG.stages.transitionDuration);
        setStageTransitionTimer(stageTransitionTimer - 1);
        setStageParticleAlpha(transitionProgress);
        if (stageTransitionTimer <= 1) {
            setIsStageTransitioning(false);
            setStageParticleAlpha(1);
        }
    }

    // ステージ名表示タイマー更新
    if (stageNameDisplayTimer > 0) {
        setStageNameDisplayTimer(stageNameDisplayTimer - 1);
    }

    // 背景描画
    // キャンバスをクリアしてThree.tsの背景が見えるようにする
    p.clear();


    // ステージ固有の背景要素
    drawStageBackgroundElements(stageCfg, transitionProgress);

    // 流れる星
    drawFlowingStars(stageCfg, dt);

    // 遷移エフェクト
    if ((isStageTransitioning || stageNameDisplayTimer > 0) && (gameState === 'playing' || gameState === 'gameover')) {
        drawStageTransitionOverlay(transitionProgress);
    }

    // レーンマーカー
    if (gameState === 'playing' || gameState === 'gameover') {
        drawLaneMarkers();
    }
}

// ============================================
// getCurrentStageCfg() - 現在のステージ設定を取得
// ============================================
export function getCurrentStageCfg() {
    const currentStageIndex = getCurrentStageIndex();
    return CONFIG.stages.backgrounds[currentStageIndex] || CONFIG.stages.backgrounds[0];
}

// ============================================
// updateStageForWave() - ウェーブに応じてステージを更新
// ============================================
export function updateStageForWave(waveNum: number): void {
    const transitions = CONFIG.stages.transitions;
    let newStageIndex = 0;
    for (let i = transitions.length - 1; i >= 0; i--) {
        if (waveNum >= transitions[i]) {
            newStageIndex = i; break;
        }
    }
    const currentStageIndex = getCurrentStageIndex();
    if (newStageIndex !== currentStageIndex) {
        setPrevStageIndex(currentStageIndex);
        setCurrentStageIndex(newStageIndex);
        setIsStageTransitioning(true);
        setStageTransitionTimer(CONFIG.stages.transitionDuration);
        setStageNameDisplayTimer(CONFIG.stages.nameDisplayDuration);
        setStageParticleAlpha(0);
        if (newStageIndex === 2) {
            setDistantGiantY(-50);
        }
    }
}

// ============================================
// drawStageTransitionOverlay() - ステージ遷移オーバーレイ
// ============================================
export function drawStageTransitionOverlay(progress: number): void {
    const p = getP5();
    const gameAreaX = CONFIG.layout.gameAreaX;
    // ステージ遷移時のフラッシュエフェクトのみ（ゲームエリア内）
    const isStageTransitioning = getIsStageTransitioning();
    if (isStageTransitioning && progress < 0.12) {
        p.fill(255, 255, 255, (1 - progress / 0.12) * 50);
        p.noStroke();
        p.rect(gameAreaX, 0, CONFIG.width, p.height);
    }
}

// ============================================
// drawStageBackgroundElements() - ステージ背景要素
// ============================================
export function drawStageBackgroundElements(stageCfg: StageConfig, _transitionProgress: number): void {
    const isStageTransitioning = getIsStageTransitioning();
    const stageParticleAlpha = getStageParticleAlpha();
    const prevStageIndex = getPrevStageIndex();
    if (isStageTransitioning && stageParticleAlpha < 1) {
        drawStageEffects(CONFIG.stages.backgrounds[prevStageIndex], 1 - stageParticleAlpha);
    }
    drawStageEffects(stageCfg, stageParticleAlpha);
}

// ============================================
// drawFlowingStars() - 流れる星を描画
// ============================================
export function drawFlowingStars(stageCfg: StageConfig, dt: number): void {
    const p = getP5();
    if (stageCfg && stageCfg.specialEffect === 'subspace') {
        return;
    }
    const dens = stageCfg ? stageCfg.starDensity : 1.0;
    const gameAreaX = CONFIG.layout.gameAreaX;
    const gameAreaW = CONFIG.width;
    for (const star of stars) {
        if (dens < 1 && (star.brightness % 100) / 100 > dens * 1.5) {
            continue;
        }
        star.y += star.speed * dt;
        if (star.y > p.height) {
            star.y = 0; star.x = gameAreaX + p.random(gameAreaW);
        }
        // 低軌道ステージ中は地球で手前の流星を遮蔽（半透明に見える問題の根本原因を解消）
        if (stageCfg && stageCfg.specialEffect === 'lowOrbit' && lowOrbitPlanetMask) {
            const dx = star.x - lowOrbitPlanetMask.x;
            const dy = star.y - lowOrbitPlanetMask.y;
            if (dx * dx + dy * dy < lowOrbitPlanetMask.r2) {
                continue;
            }
        }
        p.fill(star.brightness + p.sin(p.frameCount * 0.1 + star.x) * 30); p.noStroke();
        p.circle(star.x, star.y, star.size);
    }
}

// ============================================
// drawLaneMarkers() - レーンマーカーを描画
// ============================================
export function drawLaneMarkers() {
    const p = getP5();
    const bgCfg = CONFIG.stages.common;
    const markerCol = bgCfg.laneMarkerColor;
    const lineCol = bgCfg.flowingLineColor;
    const gameAreaCenterX = CONFIG.layout.gameAreaX + CONFIG.width / 2;

    // 中央破線（縦）- ゲームエリア中央
    p.stroke(markerCol[0], markerCol[1], markerCol[2], markerCol[3]); p.strokeWeight(2);
    for (let y = 0; y < p.height; y += 30) {
        const yo = (p.frameCount * 3) % 30;
        const x = gameAreaCenterX;
        const y1 = y + yo;
        const y2 = y1 + 15;
        // 地球の円領域では描かない（低軌道ステージのみマスクが有効）
        if (lowOrbitPlanetMask) {
            const dx = x - lowOrbitPlanetMask.x;
            const ay = lowOrbitPlanetMask.r2 - dx * dx;
            if (ay > 0) {
                const ry = p.sqrt(ay);
                const top = lowOrbitPlanetMask.y - ry;
                const bottom = lowOrbitPlanetMask.y + ry;
                const overlapStart = p.max(y1, top);
                const overlapEnd = p.min(y2, bottom);
                if (overlapStart >= overlapEnd) {
                    p.line(x, y1, x, y2);
                } else {
                    if (y1 < overlapStart) {
                        p.line(x, y1, x, overlapStart);
                    }
                    if (overlapEnd < y2) {
                        p.line(x, overlapEnd, x, y2);
                    }
                }
                continue;
            }
        }
        p.line(x, y1, x, y2);
    }

    // 流れてくる横線（中央から左右に伸びる）- ゲームエリア内
    p.stroke(lineCol[0], lineCol[1], lineCol[2], lineCol[3]); p.strokeWeight(1);
    for (let y = 0; y < p.height; y += 50) {
        const yo = (p.frameCount * 2) % 50;
        const py = y + yo, pf = p.map(py, 0, p.height, 0.3, 1);
        const x1 = gameAreaCenterX - 200 * pf;
        const x2 = gameAreaCenterX + 200 * pf;
        if (lowOrbitPlanetMask) {
            const dy = py - lowOrbitPlanetMask.y;
            const ax = lowOrbitPlanetMask.r2 - dy * dy;
            if (ax > 0) {
                const rx = p.sqrt(ax);
                const left = lowOrbitPlanetMask.x - rx;
                const right = lowOrbitPlanetMask.x + rx;
                const lx2 = p.min(x2, left);
                if (lx2 > x1) {
                    p.line(x1, py, lx2, py);
                }
                const rx1 = p.max(x1, right);
                if (x2 > rx1) {
                    p.line(rx1, py, x2, py);
                }
                continue;
            }
        }
        p.line(x1, py, x2, py);
    }
}
